// Прошивка RTS-панели: что панель сообщила о себе (get_info), отметка
// «стенд», текущее обновление, запуск и отмена. Сервер сам ничего не
// назначает — каждое обновление запускается здесь. Допустим ли образ,
// решает Шанырак; окно лишь не предлагает заведомо неподходящие.

import { useCallback, useEffect, useState } from "react";
import { gql } from "../lib/gql";
import {
  CANCEL_UPDATE_MUTATION,
  FIRMWARE_IMAGES_QUERY,
  PANEL_FIRMWARE_QUERY,
  PHASE_LABEL,
  SET_TESTBENCH_MUTATION,
  START_UPDATE_MUTATION,
  failureText,
  formatDateTime,
  hwLabel,
  hwRange,
  imageMismatch,
  phaseActive,
  type FirmwareImage,
  type FirmwareResult,
  type PanelFirmware,
} from "../lib/firmware";

// Пока обновление идёт — перечитываем состояние панели.
const ACTIVE_REFRESH_MS = 15_000;

type PanelResult = FirmwareResult & { firmware: PanelFirmware | null };

export function FirmwareModal({
  deviceId,
  title,
  onClose,
  onChanged,
}: {
  deviceId: string;
  title: string;
  onClose: () => void;
  onChanged: (fw: PanelFirmware) => void;
}) {
  const [fw, setFw] = useState<PanelFirmware | null>(null);
  const [images, setImages] = useState<FirmwareImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [imageId, setImageId] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null);

  const refresh = useCallback(async () => {
    const d = await gql<{ adminPanelFirmware: PanelFirmware[] }>(PANEL_FIRMWARE_QUERY, { deviceIds: [deviceId] });
    const next = d.adminPanelFirmware[0] ?? null;
    setFw(next);
    if (next) onChanged(next);
    return next;
  }, [deviceId, onChanged]);

  useEffect(() => {
    let cancelled = false;
    Promise.all([refresh(), gql<{ adminFirmwareImages: FirmwareImage[] }>(FIRMWARE_IMAGES_QUERY)])
      .then(([, imgs]) => {
        if (!cancelled) setImages(imgs.adminFirmwareImages);
      })
      .catch((err) => {
        if (!cancelled) setLoadError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId]);

  const active = !!fw?.update && phaseActive(fw.update.phase);
  useEffect(() => {
    if (!active) return;
    const id = window.setInterval(() => {
      if (document.visibilityState === "visible") void refresh().catch(() => undefined);
    }, ACTIVE_REFRESH_MS);
    return () => window.clearInterval(id);
  }, [active, refresh]);

  async function run(mutation: string, field: string, variables: Record<string, unknown>, okText: string) {
    setBusy(true);
    setResult(null);
    try {
      const d = await gql<Record<string, PanelResult>>(mutation, variables);
      const r = d[field];
      if (r.firmware) {
        setFw(r.firmware);
        onChanged(r.firmware);
      }
      setResult(r.success ? { ok: true, text: okText } : { ok: false, text: failureText(r) });
      return r.success;
    } catch (err) {
      setResult({ ok: false, text: err instanceof Error ? err.message : String(err) });
      return false;
    } finally {
      setBusy(false);
    }
  }

  const selected = images.find((i) => i.id === imageId) ?? null;

  async function startUpdate() {
    if (!selected) return;
    setConfirming(false);
    const ok = await run(START_UPDATE_MUTATION, "adminStartFirmwareUpdate", { deviceId, imageId: selected.id },
      `Обновление до ${selected.version} запущено. Команда уйдёт, когда панель заберёт свежие настройки.`);
    if (ok) setImageId("");
  }

  return (
    <div
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "grid", placeItems: "center", zIndex: 100 }}
      onClick={busy ? undefined : onClose}
    >
      <div className="card" style={{ maxWidth: 640, width: "92%", maxHeight: "90vh", overflowY: "auto" }} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ margin: "0 0 12px 0" }}>Прошивка панели {title}</h3>

        {loading ? (
          <div className="muted">Загружаем…</div>
        ) : loadError ? (
          <div className="error-banner">{loadError}</div>
        ) : !fw ? (
          <div className="muted">Шанырак не знает эту панель или это не RTS-панель.</div>
        ) : (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "4px 12px", fontSize: 13 }}>
              <span className="muted">Прошивка</span>
              <span style={{ fontFamily: "monospace" }}>{fw.swVersion ?? "—"}{fw.proto ? ` (протокол ${fw.proto})` : ""}</span>
              <span className="muted">Железо</span>
              <span style={{ fontFamily: "monospace" }}>
                {fw.hwVersion ?? "—"}{fw.hwCode !== null ? ` (${hwLabel(fw.hwCode)})` : ""}
              </span>
              <span className="muted">CRC загрузчика</span>
              <span style={{ fontFamily: "monospace" }}>{fw.bootCrc ?? "—"}</span>
              <span className="muted">Сведения от панели</span>
              <span>{fw.reportedAt ? formatDateTime(fw.reportedAt) : "панель ещё не присылала"}</span>
            </div>

            <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 12, fontSize: 13 }}>
              <input
                type="checkbox"
                checked={fw.testbench}
                disabled={busy}
                onChange={(e) =>
                  void run(SET_TESTBENCH_MUTATION, "adminSetPanelTestbench", { deviceId, testbench: e.target.checked },
                    e.target.checked ? "Отметка «стенд» поставлена." : "Отметка «стенд» снята.")
                }
              />
              <span>Стенд — на эту панель можно ставить dev-образы</span>
            </label>

            <h4 style={{ margin: "16px 0 6px 0" }}>Обновление</h4>
            {fw.update ? (
              <div style={{ fontSize: 13 }}>
                <div>
                  До <b style={{ fontFamily: "monospace" }}>{fw.update.target.version}</b>
                  {fw.update.fromVersion ? ` с ${fw.update.fromVersion}` : ""} —{" "}
                  <b style={{ color: fw.update.phase === "FAILED" ? "#ef4444" : fw.update.phase === "DONE" ? "#22c55e" : undefined }}>
                    {PHASE_LABEL[fw.update.phase]}
                  </b>
                  {phaseActive(fw.update.phase) && ` с ${formatDateTime(fw.update.phaseChangedAt)}`}
                </div>
                {fw.update.phase === "WAITING_CONFIG" && (
                  <div className="muted">Команда уйдёт, когда панель заберёт свежие настройки; панель не в сети — обновление ждёт её.</div>
                )}
                <div className="muted">
                  Запущено {formatDateTime(fw.update.startedAt)}
                  {fw.update.startedBy ? `, ${fw.update.startedBy}` : ""}; попыток: {fw.update.attempts}
                  {fw.update.finishedAt ? `; завершено ${formatDateTime(fw.update.finishedAt)}` : ""}
                </div>
                {fw.update.lastError && (
                  // Пока обновление идёт, ответ панели («занята», очередь) —
                  // не сбой: фаза остаётся, Шанырак повторит.
                  fw.update.phase === "FAILED" ? (
                    <div className="error-banner" style={{ marginTop: 6 }}>{fw.update.lastError}</div>
                  ) : (
                    <div className="muted">Последний ответ панели: {fw.update.lastError}</div>
                  )
                )}
                {fw.update.phase === "WAITING_CONFIG" && (
                  <button
                    type="button"
                    className="secondary"
                    style={{ marginTop: 8 }}
                    disabled={busy}
                    onClick={() => void run(CANCEL_UPDATE_MUTATION, "adminCancelFirmwareUpdate", { deviceId }, "Обновление отменено.")}
                  >
                    Отменить обновление
                  </button>
                )}
              </div>
            ) : (
              <div className="muted" style={{ fontSize: 13 }}>Обновлений ещё не было.</div>
            )}

            {!active && (
              <div style={{ marginTop: 12 }}>
                <label style={{ fontSize: 13 }}>Образ</label>
                <div className="row" style={{ gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <select value={imageId} onChange={(e) => { setImageId(e.target.value); setConfirming(false); }} disabled={busy}>
                    <option value="">— выберите образ —</option>
                    {images.map((img) => {
                      const why = imageMismatch(img, fw);
                      return (
                        <option key={img.id} value={img.id} disabled={why !== null}>
                          {img.version} · {img.kind === "DEV" ? "dev" : "выпуск"} · hw {hwRange(img)}{why ? ` — ${why}` : ""}
                        </option>
                      );
                    })}
                  </select>
                  {!confirming ? (
                    <button type="button" disabled={!selected || busy} onClick={() => setConfirming(true)}>
                      Запустить обновление
                    </button>
                  ) : (
                    <>
                      <button type="button" disabled={busy} onClick={() => void startUpdate()}>
                        Да, обновить до {selected?.version}
                      </button>
                      <button type="button" className="secondary" disabled={busy} onClick={() => setConfirming(false)}>
                        Нет
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}
          </>
        )}

        {result && (
          <div className={result.ok ? "muted" : "error-banner"} style={{ marginTop: 12, fontSize: 13, color: result.ok ? "#22c55e" : undefined }}>
            {result.text}
          </div>
        )}

        <div className="row" style={{ justifyContent: "flex-end", marginTop: 12 }}>
          <button type="button" className="secondary" onClick={onClose} disabled={busy}>
            Закрыть
          </button>
        </div>
      </div>
    </div>
  );
}
